import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { toast } from 'react-toastify';

import EmptyIcon from '@/assets/icons/empty.svg';
import Spinner from '@/components/atoms/Spinner';
import api from '@/services/apiSgft';
import type { TrainingFilesFiltersAtom } from '@/state/TrainingFilesFilter.atom';
import type { TrainingFile } from '@/types/TrainingFile';

import TrainingFileCard from './TrainingFileCard';

const PAGE_SIZE = 20;

export type TrainingFilesTabType = 'planned' | 'completed';

export interface TrainingFilesTabProps {
	type: TrainingFilesTabType;
	filteredData: TrainingFilesFiltersAtom;
	enabled: boolean;
}

export function TrainingFilesTab({
	type,
	filteredData,
	enabled,
}: TrainingFilesTabProps) {
	const { ref, inView } = useInView({ rootMargin: '160px' });

	const {
		data,
		isLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery<TrainingFile[]>({
		queryFn: async ({ pageParam = 1 }) => {
			try {
				const response = await api.get<TrainingFile[]>('/training-files', {
					params: {
						status: filteredData.status,
						suppliers: filteredData.suppliers,
						trainings: filteredData.trainings,
						startDate: filteredData.timeRange.startDate,
						endDate: filteredData.timeRange.endDate,
						trainingType: filteredData.trainingType,
						trainingFileNumber: filteredData.trainingFileNumber,
						trainingFileName: filteredData.trainingFileName,
						responsible: filteredData.responsible,
						type,
						page: pageParam,
						size: PAGE_SIZE,
					},
				});
				return response.data;
			} catch (e) {
				toast.error('Erro ao carregar os dados', {
					theme: 'colored',
					toastId: 'error',
				});
				throw e;
			}
		},
		queryKey: ['training-files', type, filteredData],
		enabled,
		retry: false,
		getNextPageParam: (lastPage, allPages) => {
			if (!Array.isArray(lastPage) || lastPage.length === 0) {
				return undefined;
			}
			if (lastPage.length < PAGE_SIZE) {
				return undefined;
			}
			return allPages.length + 1;
		},
	});

	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			void fetchNextPage();
		}
	}, [fetchNextPage, hasNextPage, inView, isFetchingNextPage]);

	const trainingFiles = data?.pages.flatMap((page) => page) ?? [];

	if (isLoading) {
		return (
			<div className="flex h-full min-h-[50vh] w-full items-center justify-center">
				<Spinner size={50} />
			</div>
		);
	}

	return (
		<div className="flex w-full min-w-min flex-col gap-2 py-2">
			{trainingFiles.length ? (
				trainingFiles.map((trainingFile, index) => (
					<TrainingFileCard
						key={`${trainingFile.id}-${index}`}
						trainingFile={trainingFile}
					/>
				))
			) : (
				<div className="flex h-full min-h-[50vh] w-full flex-col items-center justify-center rounded-md bg-white">
					<img src={EmptyIcon} className="w-40" alt="" />
					<div className="w-full text-center text-sm">
						<p>Nenhum ficheiro encontrado com esses parâmetros</p>
						<span className="font-semibold text-blue-800">
							Cadastre um ou altere os valores dos filtros para visualizar!
						</span>
					</div>
				</div>
			)}
			{hasNextPage && (
				<div
					ref={ref}
					className="flex w-full items-center justify-center py-4"
					aria-hidden
				>
					<Spinner size={30} />
				</div>
			)}
		</div>
	);
}
